import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { User, MapPin, Loader, Music, Youtube, ExternalLink, Calendar } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { getProvinceName } from '../utils/provinces';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const EXPERIENCE_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Gevorderd',
  advanced: 'Ervaren',
  professional: 'Professioneel'
};

const GOAL_LABELS = {
  hobby: 'Hobby',
  gigging: 'Optreden',
  recording: 'Opnemen',
  professional: 'Professioneel'
};

export default function Profile() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => setUser(currentUser));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      const profileSnap = await get(ref(database, `vjb_profiles/${userId}`));
      if (profileSnap.exists()) {
        setProfile(profileSnap.val());
      }

      const adsSnap = await get(ref(database, 'vjb_ads'));
      if (adsSnap.exists()) {
        const userAds = Object.entries(adsSnap.val())
          .filter(([_, ad]) => ad.createdBy === userId && ad.status === 'active')
          .map(([id, ad]) => ({ id, ...ad }));
        setAds(userAds);
      }

      setLoading(false);
    };
    loadProfile();
  }, [userId]);

  const isOwn = user?.uid === userId;

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Profiel niet gevonden</h1>
            <Link to="/zoeken" className="text-[#8146FF] hover:underline">Bekijk alle oproepen</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto px-4 md:px-6 py-8 w-full">
        {/* Profile header */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0 mx-auto md:mx-0">
              {profile.photoUrl ? (
                <img src={profile.photoUrl} alt={profile.displayName} className="w-full h-full object-cover" />
              ) : (
                <User size={48} className="text-white/40" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-3">
                <h1 className="text-2xl md:text-3xl font-bold">{profile.displayName}</h1>
                <span className="inline-block bg-white/10 text-white/60 text-xs px-3 py-1 rounded-full mx-auto md:mx-0">
                  {profile.type === 'band' ? 'Band' : 'Muzikant'}
                </span>
              </div>

              {profile.location && (
                <p className="text-white/60 flex items-center justify-center md:justify-start gap-1 mb-4">
                  <MapPin size={16} />
                  {profile.location.city}, {getProvinceName(profile.location.province)}
                </p>
              )}

              {/* Instruments */}
              {profile.instruments?.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                  {profile.instruments.map((inst, idx) => (
                    <span key={idx} className="bg-[#8146FF] text-white text-sm px-3 py-1 rounded-full">{inst}</span>
                  ))}
                </div>
              )}

              {/* Genres */}
              {profile.genres?.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {profile.genres.map((genre, idx) => (
                    <span key={idx} className="bg-white/10 text-white/70 text-sm px-3 py-1 rounded-full">{genre}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Edit button for own profile */}
            {isOwn && (
              <Link to="/profiel/bewerken" className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition text-sm hidden md:block">
                Bewerken
              </Link>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-6">
            {/* Bio */}
            {profile.bio && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h2 className="font-semibold mb-4">{profile.type === 'band' ? 'Over de band' : 'Over mij'}</h2>
                <p className="text-white/70 whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {/* Active ads */}
            {ads.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h2 className="font-semibold mb-4">Actieve oproepen</h2>
                <div className="space-y-3">
                  {ads.map((ad) => (
                    <Link
                      key={ad.id}
                      to={`/oproep/${ad.id}`}
                      className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition"
                    >
                      <h3 className="font-medium mb-2">{ad.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        {ad.lookingFor?.slice(0, 3).map((item, idx) => (
                          <span key={idx} className="bg-[#8146FF]/20 text-[#8146FF] text-xs px-2 py-1 rounded-full">{item}</span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Details</h3>
              <dl className="space-y-3">
                {profile.experienceLevel && (
                  <div>
                    <dt className="text-white/50 text-sm">Ervaring</dt>
                    <dd>{EXPERIENCE_LABELS[profile.experienceLevel] || profile.experienceLevel}</dd>
                  </div>
                )}
                {profile.goals && (
                  <div>
                    <dt className="text-white/50 text-sm">Doel</dt>
                    <dd>{GOAL_LABELS[profile.goals] || profile.goals}</dd>
                  </div>
                )}
                {profile.createdAt && (
                  <div>
                    <dt className="text-white/50 text-sm">Lid sinds</dt>
                    <dd className="flex items-center gap-1">
                      <Calendar size={14} className="text-white/50" />
                      {new Date(profile.createdAt).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Media links */}
            {(profile.mediaLinks?.youtube || profile.mediaLinks?.soundcloud || profile.mediaLinks?.spotify) && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="font-semibold mb-4">Luister</h3>
                <div className="space-y-3">
                  {profile.mediaLinks.youtube && (
                    <a href={profile.mediaLinks.youtube} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 text-white/70 hover:text-white transition">
                      <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                        <Youtube size={20} className="text-red-400" />
                      </div>
                      <span>YouTube</span>
                      <ExternalLink size={14} className="ml-auto" />
                    </a>
                  )}
                  {profile.mediaLinks.soundcloud && (
                    <a href={profile.mediaLinks.soundcloud} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 text-white/70 hover:text-white transition">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <Music size={20} className="text-orange-400" />
                      </div>
                      <span>SoundCloud</span>
                      <ExternalLink size={14} className="ml-auto" />
                    </a>
                  )}
                  {profile.mediaLinks.spotify && (
                    <a href={profile.mediaLinks.spotify} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 text-white/70 hover:text-white transition">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <Music size={20} className="text-green-400" />
                      </div>
                      <span>Spotify</span>
                      <ExternalLink size={14} className="ml-auto" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Tapsody link */}
            {profile.tapsodyLinked && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-[#8146FF]/20 flex items-center justify-center">
                  <Music size={16} className="text-[#8146FF]" />
                </div>
                <span className="text-sm text-white/60">Gebruikt Tapsody</span>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
